import { z } from "zod";
import { actionAvailabilitySchema, instantSchema } from "@amber/contracts";

export const POLICY_ACTIONS = { newVersion: "evidence.apply_policy" } as const;
export const ADMIN_POLICY_REASON = "Only Admins can change evidence-use policies";

const requirementSchema = z.enum(["required", "not_required"]);

export const policyDimensionsSchema = z.strictObject({
  original: requirementSchema,
  provenance: requirementSchema,
  language: z.strictObject({ required: z.boolean(), languages: z.array(z.string().min(2)).min(1) }),
  context: requirementSchema,
  text: requirementSchema,
  attribution: z.strictObject({ required: z.boolean(), min_unit_attribution_confidence: z.number().min(0).max(1) }),
  problems: requirementSchema,
  access: requirementSchema,
});
export type PolicyDimensionsInput = z.infer<typeof policyDimensionsSchema>;

export const POLICY_DIMENSION_KEYS = [
  "original",
  "provenance",
  "language",
  "context",
  "text",
  "attribution",
  "problems",
  "access",
] as const;
export type PolicyDimensionKey = (typeof POLICY_DIMENSION_KEYS)[number];

export const policyRuleSchema = z.strictObject({
  dimension: z.enum(POLICY_DIMENSION_KEYS),
  label: z.string(),
  rule: z.string(),
  required: z.boolean(),
});
export type PolicyRule = z.infer<typeof policyRuleSchema>;

export const policyVersionViewSchema = z.strictObject({
  id: z.string(),
  name: z.string(),
  version: z.number().int().positive(),
  state: z.enum(["applied", "superseded"]),
  provisional: z.boolean(),
  applied_at: instantSchema,
  applied_by: z.string(),
  reason: z.string(),
  dimensions: policyDimensionsSchema,
  rules: z.array(policyRuleSchema),
});
export type PolicyVersionView = z.infer<typeof policyVersionViewSchema>;

export const policiesResponseSchema = z.strictObject({
  applied: policyVersionViewSchema,
  versions: z.array(policyVersionViewSchema),
  expected_outcomes: z.array(z.string()),
  audit_count: z.number().int().nonnegative(),
  actions: z.strictObject({ new_version: actionAvailabilitySchema }),
});
export type PoliciesResponse = z.infer<typeof policiesResponseSchema>;

export const policyImpactSchema = z.strictObject({
  items: z.number().int().nonnegative(),
  eligible_to_draft: z.number().int().nonnegative(),
  draft_to_eligible: z.number().int().nonnegative(),
  communications: z.number().int().nonnegative(),
});
export type PolicyImpactView = z.infer<typeof policyImpactSchema>;

export const policyPreviewRequestSchema = z.strictObject({ dimensions: policyDimensionsSchema });

export const policyAppliedSchema = z.strictObject({
  version: z.number().int().positive(),
  reassessed: z.number().int().nonnegative(),
  impact: policyImpactSchema,
});
